import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SurveyData } from '@/constants';
import { useCurrentUser } from '@/hooks';
import {
  ACTIVE_SURVEY_STATUSES,
  SurveyResponseStatus,
  SurveyStatus,
} from '@/types';
import { PageHeaderLayout, Typography } from '@/ui';
import { resolveInsightsViewerRole, track } from '@/utils';

import type { PeerInsightsEntry } from '@/components/InsightsV2/MemberInsightsPage/types';
import type { Survey } from '@/types';

import TemplateCards from '@/components/Insights/TemplateCards';
import MemberInsightsPage from '@/components/InsightsV2/MemberInsightsPage';
import MemberInsightsSkeleton from '@/components/InsightsV2/MemberInsightsPage/MemberInsightsSkeleton';
import LockedFeature from '@/components/LockedStates';
import { useFeatureLocked } from '@/hooks/useFeatureLocked';
import {
  COACH_MEMBER_INSIGHTS_COPY,
  SELF_INSIGHTS_COPY,
  SELF_PULSE_EMPTY_DESCRIPTION,
} from '@/pages/Insights/copy';
import { useGetPulseReportsQuery } from '@/store/api/pulseApi';
import {
  useGetSurveysQuery,
  useGetSurveyTemplatesQuery,
} from '@/store/api/surveyApi';
import { useGetUserQuery } from '@/store/api/userApi';
import {
  formatMonthYear,
  mapPulsePagination,
  mapPulseReportToListEntry,
} from '@/utils/pulseReportAdapters';

const PULSE_PAGE_SIZE = 10;
const PEER_INSIGHTS_LEARN_MORE_URL = 'https://mento.co/peer-insights';
const PULSE_BASELINE_SURVEY_URL =
  'https://mento-team.typeform.com/MentoPulseCheck';

const byCreatedAtDesc = (left: Survey, right: Survey) =>
  new Date(right.createdAt).valueOf() - new Date(left.createdAt).valueOf();

const buildPeerInsightsEntries = (surveys: Survey[]): PeerInsightsEntry[] =>
  [...surveys]
    .filter(
      (survey) =>
        Boolean(survey.surveyTemplateId) &&
        survey.status !== SurveyStatus.DRAFT,
    )
    .sort((left, right) => {
      const leftDate = new Date(left.createdAt).valueOf();
      const rightDate = new Date(right.createdAt).valueOf();
      return rightDate - leftDate;
    })
    .map((survey) => {
      const responses = survey.responses ?? [];
      const respondedCount = survey.aggregated
        ? survey.aggregated.totalResponses
        : responses.filter(
            (response) => response.status === SurveyResponseStatus.COMPLETED,
          ).length;

      return {
        dateLabel: formatMonthYear(survey.completedAt || survey.createdAt),
        id: survey.id,
        respondedCount,
        surveyTemplateId: survey.surveyTemplateId,
        totalParticipants: responses.length || respondedCount,
      };
    });

const Insights = () => {
  const navigate = useNavigate();
  const { user, isUserLoading } = useCurrentUser();
  const { id: memberId } = useParams<{ id?: string }>();
  const { isFeatureLocked } = useFeatureLocked(user);
  const [pulsePage, setPulsePage] = useState(1);

  const isCoachMemberView = Boolean(memberId);
  const showInsightsV2 = isCoachMemberView ? !!user.insightsV2Enabled : true;
  const { data: subjectUser, isLoading: isSubjectLoading } = useGetUserQuery(
    memberId ?? '',
    { skip: !memberId || !user.id },
  );

  const insightsSubjectUser = isCoachMemberView ? subjectUser : user;

  useEffect(() => {
    setPulsePage(1);
  }, [insightsSubjectUser?.id]);

  const surveysQueryArg = insightsSubjectUser?.id
    ? { userId: insightsSubjectUser.id }
    : skipToken;

  const { data: surveys = [], isLoading: isSurveysLoading } =
    useGetSurveysQuery(surveysQueryArg, {
      skip: !showInsightsV2 || !insightsSubjectUser?.id,
    });

  const { data: pulseReportsData, isLoading: isPulseLoading } =
    useGetPulseReportsQuery(
      {
        memberId: insightsSubjectUser?.id || '',
        page: pulsePage,
        per: PULSE_PAGE_SIZE,
      },
      {
        skip: !showInsightsV2 || !insightsSubjectUser?.id,
      },
    );
  const { data: selfSurveyTemplates = [], isLoading: isTemplatesLoading } =
    useGetSurveyTemplatesQuery(
      {
        userId: insightsSubjectUser?.id,
      },
      {
        skip: !showInsightsV2 || isCoachMemberView || !insightsSubjectUser?.id,
      },
    );

  if (isUserLoading || !user.id) {
    return null;
  }

  if (isCoachMemberView && (isSubjectLoading || !insightsSubjectUser?.id)) {
    return null;
  }

  if (isFeatureLocked) {
    return (
      <div
        className={clsx('p-4 lg:px-32 lg:pt-8', {
          'w-full': isFeatureLocked,
        })}
      >
        <PageHeaderLayout title="Insights" />
        <LockedFeature title="insights" />
      </div>
    );
  }

  if (showInsightsV2) {
    const isSelfView = !isCoachMemberView;

    if (isSurveysLoading || isPulseLoading || isTemplatesLoading) {
      return (
        <MemberInsightsSkeleton
          centered={isSelfView}
          heading={
            (isSelfView ? SELF_INSIGHTS_COPY : COACH_MEMBER_INSIGHTS_COPY)
              .heading
          }
        />
      );
    }
    const hasExactlyOneAssignedCoach = user.coaches.length === 1;
    const showPulseCheckAction =
      isSelfView &&
      hasExactlyOneAssignedCoach &&
      pulseReportsData !== undefined;
    const pulseCheckAction = showPulseCheckAction
      ? {
          label: 'Take Pulse Check Survey',
          onClick: () => {
            window.open(
              PULSE_BASELINE_SURVEY_URL,
              '_blank',
              'noopener,noreferrer',
            );
          },
        }
      : undefined;
    const pulseEntries = (pulseReportsData?.reports ?? []).map((report) =>
      mapPulseReportToListEntry(report, {
        includeOnboardingReviewPresentation: true,
        onboardingReviewDetailPath: isCoachMemberView
          ? (onboardingReport) =>
              `/coach/members/${memberId}/onboarding-review/${onboardingReport.reportId}`
          : undefined,
      }),
    );
    const peerInsightsEntries = buildPeerInsightsEntries(surveys);
    const viewerRole = resolveInsightsViewerRole(user, isSelfView);
    const copy = isSelfView ? SELF_INSIGHTS_COPY : COACH_MEMBER_INSIGHTS_COPY;
    const selfPeerInsightsTemplate = isSelfView
      ? selfSurveyTemplates[0]
      : undefined;
    const activePeerInsightsSurvey = isSelfView
      ? surveys.find((survey) => ACTIVE_SURVEY_STATUSES.includes(survey.status))
      : undefined;
    const mostRecentSharedPeerInsightsSurvey = isSelfView
      ? [...surveys]
          .filter((survey) => survey.status === SurveyStatus.SHARED)
          .sort(byCreatedAtDesc)[0]
      : undefined;
    const repeatLaunchTemplate =
      isSelfView && mostRecentSharedPeerInsightsSurvey
        ? selfSurveyTemplates.find(
            (template) =>
              template.id ===
              mostRecentSharedPeerInsightsSurvey.surveyTemplateId,
          )
        : undefined;
    const activePeerInsightsTemplate = isSelfView
      ? selfSurveyTemplates.find(
          (template) =>
            template.id === activePeerInsightsSurvey?.surveyTemplateId,
        )
      : undefined;
    const activePeerInsightsSurveyVisible = Boolean(
      activePeerInsightsSurvey &&
      peerInsightsEntries.some(
        (entry) => entry.id === activePeerInsightsSurvey.id,
      ),
    );
    const hideSelfPeerInsightsPanel =
      isSelfView &&
      peerInsightsEntries.length === 0 &&
      !selfPeerInsightsTemplate;
    const showRepeatLaunchAction =
      isSelfView &&
      peerInsightsEntries.length > 0 &&
      Boolean(
        (activePeerInsightsSurvey?.surveyTemplateId &&
          !activePeerInsightsSurveyVisible) ||
        (!activePeerInsightsSurvey && repeatLaunchTemplate),
      );

    return (
      <MemberInsightsPage
        centered={isSelfView}
        copy={copy}
        peerInsights={{
          entries: peerInsightsEntries,
          emptyState:
            isSelfView &&
            peerInsightsEntries.length === 0 &&
            selfPeerInsightsTemplate
              ? {
                  description: SurveyData.clientData.description,
                  media: {
                    alt: 'Peer insights demo',
                    src: SurveyData.clientData.mediaUrl,
                    type: 'video',
                  },
                  primaryAction: {
                    label: SurveyData.clientData.button,
                    onClick: () => {
                      track('Clicked Survey', {
                        surveyTemplate: selfPeerInsightsTemplate.name,
                      });
                      navigate(`/m/insights/${selfPeerInsightsTemplate.id}`);
                    },
                  },
                  secondaryAction: {
                    label: 'Learn more',
                    onClick: () => {
                      window.open(
                        PEER_INSIGHTS_LEARN_MORE_URL,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    },
                  },
                }
              : undefined,
          headerAction: showRepeatLaunchAction
            ? {
                label: '+ New',
                onClick: () => {
                  if (activePeerInsightsSurvey?.surveyTemplateId) {
                    track('Clicked Survey In Progress', {
                      surveyTemplate:
                        activePeerInsightsTemplate?.name ||
                        activePeerInsightsSurvey.title,
                    });
                    navigate(
                      `/m/insights/${activePeerInsightsSurvey.surveyTemplateId}`,
                    );
                    return;
                  }

                  track('Clicked Survey', {
                    surveyTemplate: repeatLaunchTemplate!.name,
                  });
                  navigate(`/m/insights/${repeatLaunchTemplate!.id}/new`);
                },
              }
            : undefined,
          hidden: hideSelfPeerInsightsPanel,
        }}
        pulseCheck={{
          entries: pulseEntries,
          emptyState:
            isSelfView && pulseEntries.length === 0
              ? {
                  description: SELF_PULSE_EMPTY_DESCRIPTION,
                  primaryAction: pulseCheckAction,
                }
              : undefined,
          headerAction: pulseEntries.length > 0 ? pulseCheckAction : undefined,
          pagination: mapPulsePagination(pulseReportsData?.pagination),
        }}
        viewerRole={viewerRole}
        onPeerViewClick={(entry) => {
          if (!entry.surveyTemplateId) {
            return;
          }
          navigate(
            isCoachMemberView
              ? `/coach/members/${memberId}/insights/${entry.surveyTemplateId}`
              : `/m/insights/${entry.surveyTemplateId}`,
          );
        }}
        onPulsePageChange={(page) => {
          setPulsePage(page);
        }}
        onPulseViewClick={(entry) => {
          navigate(
            entry.detailPath ||
              (isCoachMemberView
                ? `/coach/members/${memberId}/pulse/${entry.id}`
                : `/m/pulse/${entry.id}`),
          );
        }}
      />
    );
  }

  if (!isCoachMemberView) {
    return null;
  }

  return (
    <div
      className={clsx('p-4 lg:px-32 lg:pt-8', {
        container: true,
      })}
    >
      <PageHeaderLayout title="Insights" />
      <Typography className="pb-3 text-cement-400">
        Build awareness through self-assessments and insights from your peers
        and team to accelerate your growth.
      </Typography>
      <TemplateCards subjectUser={insightsSubjectUser!} />
    </div>
  );
};

export default Insights;
